// Транспорт и хуки раздела «Неликвиды склада». ТОЛЬКО ЧТЕНИЕ.
//
// Отдельный файл, а не api/pex.ts: там отчёты руководителя (дебиторка, косяки, отделы),
// дверь у них своя. Здесь склад — другой раздел прав и другая аудитория. Сложить их
// в один файл значило бы однажды переиспользовать хук не там.
//
// ДВА ИСТОЧНИКА В ОДНОМ РАЗДЕЛЕ, И ЭТО НАМЕРЕННО.
//   • остатки и возраст — из зеркала (ядро, /api/nl/…). Это те же данные и та же формула,
//     что у кабинета руководителя; цифры обязаны совпадать;
//   • резерв под заказ — ЖИВЬЁМ из 1С через прокси /api/1c/ut. Панель регистр резервов
//     не выгружает вовсе, в зеркале его нет, и придумать резерв из остатка нельзя.
// Поэтому вкладка резерва может отстать от остальных и обязана показывать свою свежесть
// отдельно — этим занимается OdataState, как на экране «Склад».
import { useQueries, useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'
import { odataGet, odataRetry } from './odata'
import type { OdataMeta } from './odata'
import { пачками, резервПоЗаказам } from '../lib/nl'
import type {
  ЗаказПодРезерв, Позиция, РезервПоЗаказу, Свод, Список, СправочникиNL, СтрокаРезерва,
} from '../lib/nl'
// Единственный сборщик путей OData в проекте. Своего заводить нельзя: кодирование
// кириллицы в пути уже ломалось однажды, и чинить это в двух местах — значит починить
// в одном.
import { odataPath } from '../lib/stock'

const БАЗА = '/api/nl'

/** Тот же разбор ответа, что в api/client.ts, api/pbh.ts и api/p73x.ts. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 — кривой фильтр. Его надо ОБЪЯСНИТЬ, а не показать пустую таблицу: пустая
    // таблица здесь читается как «неликвида нет», и это самая дорогая ложь раздела.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

export interface ФильтрыNL {
  [ключ: string]: unknown
  days?: number
  region?: string
  wh?: string
  q?: string
  dead?: boolean
  sort?: string
  start?: number
  limit?: number
}

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

export function useNLRefs() {
  return useQuery<СправочникиNL>({
    queryKey: ['nl', 'refs'],
    queryFn: () => запрос<СправочникиNL>('/refs'),
    ...ОБЩЕЕ,
  })
}

export function useNLСвод(ф: ФильтрыNL) {
  return useQuery<Свод>({
    queryKey: ['nl', 'summary', ф],
    queryFn: () => запрос<Свод>('/summary' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

export function useNLПозиции(ф: ФильтрыNL) {
  return useQuery<Список<Позиция> & { days: number; sort: string; history_from: string }>({
    queryKey: ['nl', 'items', ф],
    queryFn: () => запрос('/items' + строкаЗапроса(ф)),
    ...ОБЩЕЕ,
  })
}

// ─────────────────────── резерв: живой регистр 1С + зеркало заказов ───────────────────────

/**
 * Путь к регистру резервов. Собирается тем же odataPath, что и на экране «Склад»:
 * кодировать путь должен вызывающий, ядро его не трогает (иначе ломается кириллица).
 *
 * Без `$select` НАМЕРЕННО: 1С этой конфигурации отвечает «Сегмент пути ВРезервеBalance
 * не найден» на попытку выбрать колонки виртуальной таблицы Balance() — проверено на бою
 * 27.07.2026. Регистр небольшой (сотни строк), лишний трафик здесь дешевле лишней хитрости.
 */
const РЕЗЕРВ_ПУТЬ = odataPath('AccumulationRegister_ТоварыКОтгрузке/Balance()',
                              { $format: 'json' })

export interface Резерв {
  orders: РезервПоЗаказу[]
  meta?: OdataMeta
}

/**
 * Резервные строки живьём из 1С. Ядро держит ответ в своём кеше (минимум 30 с),
 * поэтому просим не свежее трёх минут: чаще этого резервы всё равно не пересчитываются,
 * а каждый промах мимо кеша — это очередь к 1С, общая на всю CRM.
 */
export function useРезерв(включено = true) {
  return useQuery<Резерв>({
    queryKey: ['nl', 'reserve'],
    enabled: включено,
    queryFn: async () => {
      const r = await odataGet<СтрокаРезерва>('ut', РЕЗЕРВ_ПУТЬ, 180)
      return { orders: резервПоЗаказам(r.rows), meta: r.meta }
    },
    retry: odataRetry,
    staleTime: 120_000,
  })
}

/**
 * Заказы клиентов по ссылкам из резерва. Ссылок бывает больше, чем ядро принимает
 * за раз (предел 60 — это длина URL, а не каприз), поэтому спрашиваем пачками
 * параллельно и склеиваем.
 *
 * `missing` не выбрасываем: заказ, которого нет в зеркале, — это резерв под документ,
 * которого CRM не видит. Такое надо показать, а не молча потерять строку.
 */
export function useЗаказыПодРезерв(ссылки: string[]) {
  const пачки = пачками(ссылки)
  const запросы = useQueries({
    queries: пачки.map((часть) => ({
      queryKey: ['nl', 'orders', часть],
      queryFn: () => запрос<{ rows: ЗаказПодРезерв[]; total: number; missing: string[] }>(
        '/orders?ref=' + часть.join(','),
      ),
      ...ОБЩЕЕ,
    })),
  })
  return {
    isLoading: запросы.some((з) => з.isLoading),
    error: запросы.find((з) => з.error)?.error ?? null,
    rows: запросы.flatMap((з) => з.data?.rows ?? []),
    missing: запросы.flatMap((з) => з.data?.missing ?? []),
  }
}
